import { Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/auth'
import { useUIStore } from './store/ui'
import { lazyWithRetry } from './lib/lazyWithRetry'
import PageLoader from './components/ui/PageLoader'
import './App.css'

const TrocarSenhaPage  = lazyWithRetry(() => import('./pages/auth/TrocarSenhaPage'))
const PrivacidadePage  = lazyWithRetry(() => import('./pages/legal/PrivacidadePage'))
const TermosPage       = lazyWithRetry(() => import('./pages/legal/TermosPage'))

const AppLayout                 = lazyWithRetry(() => import('./components/layout/AppLayout'))
const LoginPage                 = lazyWithRetry(() => import('./pages/auth/LoginPage'))
const EsqueciSenhaPage          = lazyWithRetry(() => import('./pages/auth/EsqueciSenhaPage'))
const RedefinirSenhaPage        = lazyWithRetry(() => import('./pages/auth/RedefinirSenhaPage'))
const MenuPage                  = lazyWithRetry(() => import('./pages/MenuPage'))
const FinanceiroPage            = lazyWithRetry(() => import('./pages/financeiro/FinanceiroPage'))
const TransacoesPage            = lazyWithRetry(() => import('./pages/financeiro/TransacoesPage'))
const NovaTransacaoPage         = lazyWithRetry(() => import('./pages/financeiro/NovaTransacaoPage'))
const RelatorioFinanceiroPage   = lazyWithRetry(() => import('./pages/financeiro/RelatorioFinanceiroPage'))
const ContasPagarPage           = lazyWithRetry(() => import('./pages/financeiro/ContasPagarPage'))
const FrequenciaPage            = lazyWithRetry(() => import('./pages/frequencia/FrequenciaPage'))
const RegistrosFrequenciaPage   = lazyWithRetry(() => import('./pages/frequencia/RegistrosFrequenciaPage'))
const NovaFrequenciaPage        = lazyWithRetry(() => import('./pages/frequencia/NovaFrequenciaPage'))
const PessoasPage               = lazyWithRetry(() => import('./pages/pessoas/PessoasPage'))
const KanbanVisitantesPage      = lazyWithRetry(() => import('./pages/visitantes/KanbanVisitantesPage'))
const ConfigPage                = lazyWithRetry(() => import('./pages/config/ConfigPage'))
const NotificacoesConfigPage    = lazyWithRetry(() => import('./pages/config/NotificacoesConfigPage'))
const WhatsAppConectarPage      = lazyWithRetry(() => import('./pages/whatsapp/WhatsAppConectarPage'))
const WhatsAppEventosPage       = lazyWithRetry(() => import('./pages/whatsapp/WhatsAppEventosPage'))
const WhatsAppRelatorioPage     = lazyWithRetry(() => import('./pages/whatsapp/WhatsAppRelatorioPage'))
const IgrejasPage               = lazyWithRetry(() => import('./pages/igrejas/IgrejasPage'))
const UsuariosPage              = lazyWithRetry(() => import('./pages/usuarios/UsuariosPage'))
const CelulasPage               = lazyWithRetry(() => import('./pages/celulas/CelulasPage'))
const CadastroCelulasPage       = lazyWithRetry(() => import('./pages/celulas/CadastroCelulasPage'))
const NovaCelulaPage            = lazyWithRetry(() => import('./pages/celulas/NovaCelulaPage'))
const RelatoriosCelulaPage      = lazyWithRetry(() => import('./pages/celulas/RelatoriosCelulaPage'))
const TodosRelatoriosPage       = lazyWithRetry(() => import('./pages/celulas/TodosRelatoriosPage'))
const NovoRelatorioCelulaPage   = lazyWithRetry(() => import('./pages/celulas/NovoRelatorioCelulaPage'))
const DetalheCelulaPage         = lazyWithRetry(() => import('./pages/celulas/DetalheCelulaPage'))
const CultosPage                = lazyWithRetry(() => import('./pages/cultos/CultosPage'))
const NovoCultoPage             = lazyWithRetry(() => import('./pages/cultos/NovoCultoPage'))
const DetalheCultoPage          = lazyWithRetry(() => import('./pages/cultos/DetalheCultoPage'))
const LogsPage                  = lazyWithRetry(() => import('./pages/admin/LogsPage'))
const CadastroMembroPage        = lazyWithRetry(() => import('./pages/cadastro/CadastroMembroPage'))
const AgendaPage                = lazyWithRetry(() => import('./pages/agenda/AgendaPage'))
const AgendaEventosPage         = lazyWithRetry(() => import('./pages/agenda/AgendaEventosPage'))
const AgendaAniversariantesPage = lazyWithRetry(() => import('./pages/agenda/AgendaAniversariantesPage'))
const NovoEventoPage            = lazyWithRetry(() => import('./pages/agenda/NovoEventoPage'))
const DetalheEventoPage         = lazyWithRetry(() => import('./pages/agenda/DetalheEventoPage'))
const ContatosPage              = lazyWithRetry(() => import('./pages/contatos/ContatosPage'))

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const usuario = useAuthStore(s => s.usuario)
  if (usuario?.funcao !== 'SuperAdmin') return <Navigate to="/" replace />
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const usuario = useAuthStore(s => s.usuario)
  if (usuario?.funcao !== 'Admin' && usuario?.funcao !== 'SuperAdmin') return <Navigate to="/" replace />
  return <>{children}</>
}

function PermissaoRoute({ children, recurso, acao }: { children: React.ReactNode; recurso: string; acao: string }) {
  const canDo  = useAuthStore(s => s.canDo)
  const isAdmin = useAuthStore(s => s.usuario?.funcao === 'Admin' || s.usuario?.funcao === 'SuperAdmin')
  if (!isAdmin && !canDo(recurso, acao)) return <Navigate to="/" replace />
  return <>{children}</>
}

function TrocarSenhaRoute() {
  const token = useAuthStore(s => s.token)
  if (!token) return <Navigate to="/login" replace />
  return <TrocarSenhaPage />
}

export default function App() {
  const theme = useUIStore(s => s.theme)

  // Mantém data-theme no <html> em sync com o Zustand (inclusive após rehydration do persist)
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('ct-theme', theme)
  }, [theme])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'ct-theme' && (e.newValue === 'light' || e.newValue === 'dark')) {
        useUIStore.getState().setTheme(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login"              element={<LoginPage />} />
          <Route path="/esqueci-senha"    element={<EsqueciSenhaPage />} />
          <Route path="/redefinir-senha"  element={<RedefinirSenhaPage />} />
          <Route path="/trocar-senha"     element={<TrocarSenhaRoute />} />
          <Route path="/cadastro/:slug"   element={<CadastroMembroPage />} />
          <Route path="/privacidade"      element={<PrivacidadePage />} />
          <Route path="/termos"           element={<TermosPage />} />
          <Route element={<AppLayout />}>
            <Route path="/" element={<MenuPage />} />

            {/* Financeiro */}
            <Route path="/financeiro"           element={<PermissaoRoute recurso="transacoes" acao="visualizar"><FinanceiroPage /></PermissaoRoute>} />
            <Route path="/financeiro/novo"      element={<PermissaoRoute recurso="transacoes" acao="criar"><NovaTransacaoPage /></PermissaoRoute>} />
            <Route path="/financeiro/relatorio" element={<PermissaoRoute recurso="transacoes" acao="visualizar"><RelatorioFinanceiroPage /></PermissaoRoute>} />
            <Route path="/financeiro/contas-pagar" element={<PermissaoRoute recurso="transacoes" acao="visualizar"><ContasPagarPage /></PermissaoRoute>} />
            <Route path="/financeiro/:ano/:mes" element={<PermissaoRoute recurso="transacoes" acao="visualizar"><TransacoesPage /></PermissaoRoute>} />

            {/* Cultos — entidade pai de Financeiro e Frequência */}
            <Route path="/cultos"      element={<PermissaoRoute recurso="cultos" acao="visualizar"><CultosPage /></PermissaoRoute>} />
            <Route path="/cultos/novo" element={<PermissaoRoute recurso="cultos" acao="criar"><NovoCultoPage /></PermissaoRoute>} />
            <Route path="/cultos/:id"  element={<PermissaoRoute recurso="cultos" acao="visualizar"><DetalheCultoPage /></PermissaoRoute>} />

            {/* Agenda — hub com calendário, eventos e aniversariantes */}
            <Route path="/agenda"                          element={<PermissaoRoute recurso="agenda" acao="visualizar"><AgendaPage /></PermissaoRoute>} />
            <Route path="/agenda/eventos"                  element={<PermissaoRoute recurso="agenda" acao="visualizar"><AgendaEventosPage /></PermissaoRoute>} />
            {/* Aniversariantes expõe dados pessoais (nascimento, telefone) — exige pessoas:visualizar */}
            <Route path="/agenda/aniversariantes"          element={<PermissaoRoute recurso="pessoas" acao="visualizar"><AgendaAniversariantesPage /></PermissaoRoute>} />
            <Route path="/agenda/novo"                     element={<PermissaoRoute recurso="agenda" acao="criar"><NovoEventoPage /></PermissaoRoute>} />
            <Route path="/agenda/:id"                      element={<PermissaoRoute recurso="agenda" acao="visualizar"><DetalheEventoPage /></PermissaoRoute>} />

            {/* Frequência */}
            <Route path="/frequencia"               element={<PermissaoRoute recurso="frequencias" acao="visualizar"><FrequenciaPage /></PermissaoRoute>} />
            <Route path="/frequencia/novo"          element={<PermissaoRoute recurso="frequencias" acao="criar"><NovaFrequenciaPage /></PermissaoRoute>} />
            <Route path="/frequencia/:ano/:mes_num" element={<PermissaoRoute recurso="frequencias" acao="visualizar"><RegistrosFrequenciaPage /></PermissaoRoute>} />

            {/* Pessoas — cadastro unificado (membros, visitantes, líderes, pastores) */}
            <Route path="/pessoas" element={<PermissaoRoute recurso="pessoas" acao="visualizar"><PessoasPage /></PermissaoRoute>} />
            <Route path="/funil"   element={<PermissaoRoute recurso="funil" acao="visualizar"><KanbanVisitantesPage /></PermissaoRoute>} />

            {/* Contatos externos para automações WhatsApp */}
            <Route path="/contatos" element={<PermissaoRoute recurso="contatos" acao="visualizar"><ContatosPage /></PermissaoRoute>} />

            {/* Células — módulo */}
            <Route path="/celulas"                           element={<PermissaoRoute recurso="celulas" acao="visualizar"><CelulasPage /></PermissaoRoute>} />
            <Route path="/celulas/cadastro"                  element={<PermissaoRoute recurso="celulas" acao="visualizar"><CadastroCelulasPage /></PermissaoRoute>} />
            <Route path="/celulas/nova"                      element={<PermissaoRoute recurso="celulas" acao="criar"><NovaCelulaPage /></PermissaoRoute>} />
            <Route path="/celulas/relatorios"                element={<PermissaoRoute recurso="celulas" acao="visualizar"><TodosRelatoriosPage /></PermissaoRoute>} />
            <Route path="/celulas/relatorios/novo"           element={<PermissaoRoute recurso="celulas" acao="criar"><NovoRelatorioCelulaPage /></PermissaoRoute>} />
            <Route path="/celulas/detalhe/:celulaId"         element={<PermissaoRoute recurso="celulas" acao="visualizar"><DetalheCelulaPage /></PermissaoRoute>} />
            <Route path="/celulas/:celulaId/relatorios"      element={<PermissaoRoute recurso="celulas" acao="visualizar"><RelatoriosCelulaPage /></PermissaoRoute>} />
            <Route path="/celulas/:celulaId/relatorios/novo" element={<PermissaoRoute recurso="celulas" acao="criar"><NovoRelatorioCelulaPage /></PermissaoRoute>} />
            <Route path="/funil/config"                    element={<AdminRoute><ConfigPage /></AdminRoute>} />
            <Route path="/config/notificacoes"            element={<AdminRoute><NotificacoesConfigPage /></AdminRoute>} />
            <Route path="/whatsapp/conectar"              element={<AdminRoute><WhatsAppConectarPage /></AdminRoute>} />
            <Route path="/whatsapp/eventos"               element={<PermissaoRoute recurso="whatsapp" acao="visualizar"><WhatsAppEventosPage /></PermissaoRoute>} />
            <Route path="/whatsapp/relatorio"             element={<PermissaoRoute recurso="whatsapp" acao="visualizar"><WhatsAppRelatorioPage /></PermissaoRoute>} />
            <Route path="/membros"                element={<Navigate to="/pessoas?tipo=MEMBRO"    replace />} />
            <Route path="/visitantes"             element={<Navigate to="/pessoas?tipo=VISITANTE" replace />} />

            {/* Igrejas — superadmin only */}
            <Route path="/igrejas"                element={<SuperAdminRoute><IgrejasPage /></SuperAdminRoute>} />

            {/* Usuários — admin only */}
            <Route path="/usuarios"               element={<AdminRoute><UsuariosPage /></AdminRoute>} />

            {/* Log de alterações — admin only */}
            <Route path="/logs"                   element={<AdminRoute><LogsPage /></AdminRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
